import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import BottomNav from '../components/ui/BottomNav'
import TopBar from '../components/ui/TopBar'
import Panel from '../components/ui/Panel'
import Avatar from '../components/ui/Avatar'
import PickSegment from '../components/ui/PickSegment'
import { buttonClasses } from '../components/ui/Button'
import GameCurve from '../components/charts/GameCurve'
import BarList from '../components/charts/BarList'
import type { BarListItem } from '../components/charts/BarList'
import Matrix from '../components/charts/Matrix'
import CountMatrix from '../components/charts/CountMatrix'
import BidHeatmap from '../components/charts/BidHeatmap'
import type { BidHeatmapItem } from '../components/charts/BidHeatmap'
import StatTile from '../components/charts/StatTile'
import statsTexts from '../texts.stats'
import { getClubs, getGames } from '../lib/api'
import type { Club, GameSummary } from '../lib/api'
import { getClubStats, getGameDetail, getMyStats } from '../lib/api.stats'
import type { ClubStats, MyStats, StatsGameDetail } from '../lib/api.stats'
import { computeGameStats } from '../lib/gameStats'
import { SUITS } from '../lib/scoring'
import type { TrumpSuit } from '../../../shared/types'

type Mode = 'season' | 'game'

interface PlayerStatsRow {
  userId: number
  name: string
  asBidder: { count: number; won: number }
  asPartner: { count: number; won: number }
  solos: { count: number; won: number }
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)} %`
}

function toneOf(n: number): 'mint' | 'red' | 'default' {
  return n > 0 ? 'mint' : n < 0 ? 'red' : 'default'
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

/** Bruges til at gætte, om et spil har runder endnu, uden en ekstra hentning pr. spil. */
function hasRounds(game: GameSummary): boolean {
  return !game.players.every((p) => p.totalScore === 0)
}

function PlayersStatsTable({ players }: { players: PlayerStatsRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[420px] border-collapse text-[0.8em]">
        <thead>
          <tr className="text-left text-[0.65rem] uppercase tracking-[0.04em] text-text-2/70">
            <th className="whitespace-nowrap py-1 pr-2">{statsTexts.playersColName}</th>
            <th className="whitespace-nowrap py-1 pr-2 text-right">{statsTexts.playersColBids}</th>
            <th className="whitespace-nowrap py-1 pr-2 text-right">{statsTexts.playersColWon}</th>
            <th className="whitespace-nowrap py-1 pr-2 text-right">{statsTexts.playersColPartner}</th>
            <th className="whitespace-nowrap py-1 text-right">{statsTexts.playersColSolo}</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.userId} className="border-t border-text-2/10">
              <td className="min-w-[90px] truncate py-1.5 pr-2">{p.name}</td>
              <td className="py-1.5 pr-2 text-right font-mono">{p.asBidder.count}</td>
              <td className="py-1.5 pr-2 text-right font-mono">{p.asBidder.won}</td>
              <td className="py-1.5 pr-2 text-right font-mono">{p.asPartner.count}</td>
              <td className="py-1.5 text-right font-mono">{p.solos.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function buildTrumpItems(trumps: Array<{ suit: TrumpSuit; count: number; won: number }>): BarListItem[] {
  const bySuit = new Map(trumps.map((t) => [t.suit, t]))
  const total = trumps.reduce((sum, t) => sum + t.count, 0)
  return SUITS.map(([suit]) => {
    const t = bySuit.get(suit)
    const count = t?.count ?? 0
    const won = t?.won ?? 0
    const winRate = count === 0 ? 0 : won / count
    return {
      key: suit,
      label: suit,
      value: total > 0 ? (count / total) * 100 : 0,
      valueLabel: statsTexts.trumpValueLabel(count, winRate),
      detail: count === 0 ? undefined : statsTexts.wonOf(won, count)
    }
  })
}

function Stats() {
  const [loadingClubs, setLoadingClubs] = useState(true)
  const [clubs, setClubs] = useState<Club[]>([])
  const [clubId, setClubId] = useState<number | null>(null)
  const [games, setGames] = useState<GameSummary[]>([])

  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [mode, setMode] = useState<Mode>('season')

  const [stats, setStats] = useState<ClubStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const [statsError, setStatsError] = useState(false)

  const [seasonCurveGame, setSeasonCurveGame] = useState<StatsGameDetail | null>(null)

  const [selectedGameId, setSelectedGameId] = useState<number | null>(null)
  const [selectedGameDetail, setSelectedGameDetail] = useState<StatsGameDetail | null>(null)

  const [myStats, setMyStats] = useState<MyStats | null>(null)

  useEffect(() => {
    getClubs()
      .then((cs) => {
        setClubs(cs)
        if (cs.length > 0) setClubId(cs[0].id)
      })
      .finally(() => setLoadingClubs(false))
    getGames()
      .then((r) => setGames(r.games))
      .catch(() => {
        // Pointkurve og spilliste forbliver tomme, hvis spillene ikke kan hentes
      })
    getMyStats()
      .then(setMyStats)
      .catch(() => {
        // Profilsektionen skjules, hvis den ikke kan hentes
      })
  }, [])

  // Skift til klubbens eget år og nulstil spilvalg, når man vælger en anden klub
  useEffect(() => {
    setYear(currentYear)
    setSelectedGameId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId])

  useEffect(() => {
    if (!clubId) return
    setLoadingStats(true)
    setStatsError(false)
    getClubStats(clubId, year)
      .then(setStats)
      .catch(() => setStatsError(true))
      .finally(() => setLoadingStats(false))
  }, [clubId, year])

  const gamesInClub = clubId ? games.filter((g) => g.clubId === clubId) : []
  const sortedGamesInClub = [...gamesInClub].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  // (a) Sæson-pointkurve: nyeste spil med mindst to runder, højst 5 forsøg
  useEffect(() => {
    if (!clubId) {
      setSeasonCurveGame(null)
      return
    }
    let cancelled = false
    setSeasonCurveGame(null)
    const candidates = sortedGamesInClub.slice(0, 5)
    ;(async () => {
      for (const g of candidates) {
        try {
          const detail = await getGameDetail(g.id)
          if (cancelled) return
          if (detail.rounds.length >= 2) {
            setSeasonCurveGame(detail)
            return
          }
        } catch {
          // Prøv næste spil
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, games])

  // Standardvalg i spil-tilstand: nyeste spil med runder
  useEffect(() => {
    if (!clubId) return
    if (selectedGameId !== null && sortedGamesInClub.some((g) => g.id === selectedGameId)) return
    const withRounds = sortedGamesInClub.find(hasRounds)
    setSelectedGameId((withRounds ?? sortedGamesInClub[0])?.id ?? null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, games])

  useEffect(() => {
    if (!selectedGameId) {
      setSelectedGameDetail(null)
      return
    }
    let cancelled = false
    getGameDetail(selectedGameId)
      .then((d) => {
        if (!cancelled) setSelectedGameDetail(d)
      })
      .catch(() => {
        if (!cancelled) setSelectedGameDetail(null)
      })
    return () => {
      cancelled = true
    }
  }, [selectedGameId])

  if (loadingClubs) {
    return (
      <Screen>
        <TopBar title={statsTexts.title} />
        <p className="text-text-2">{statsTexts.loading}</p>
        <BottomNav current="stats" />
      </Screen>
    )
  }

  if (clubs.length === 0) {
    return (
      <Screen>
        <TopBar title={statsTexts.title} />
        <Panel>
          <b>{statsTexts.needClubTitle}</b>
          <p className="text-[0.85em] text-text-2">{statsTexts.needClubHint}</p>
          <Link to="/klubber" className={buttonClasses({ variant: 'ghost' })}>
            {statsTexts.needClubLink}
          </Link>
        </Panel>
        <BottomNav current="stats" />
      </Screen>
    )
  }

  const years = Array.from(new Set([currentYear, ...gamesInClub.map((g) => new Date(g.createdAt).getFullYear())])).sort(
    (a, b) => b - a
  )

  const bidHeatmapItems: BidHeatmapItem[] = (stats?.bids ?? []).map((b) => ({ bidType: b.bidType, count: b.count }))
  const bidsTotal = bidHeatmapItems.reduce((sum, b) => sum + b.count, 0)

  const trumpItems: BarListItem[] = buildTrumpItems(stats?.trumps ?? [])

  const matrixPlayers = (stats?.players ?? []).map((p) => ({ id: p.userId, name: p.name }))
  const cellFor = (a: number, b: number) => {
    const entry = stats?.partners.find((p) => (p.a === a && p.b === b) || (p.a === b && p.b === a))
    return entry ? { points: entry.points, rounds: entry.rounds, won: entry.won } : null
  }
  const bidderPartnerCellFor = (bidderId: number, partnerId: number) => {
    const entry = stats?.bidderPartners.find((p) => p.bidderId === bidderId && p.partnerId === partnerId)
    return entry ? { count: entry.count, won: entry.won } : null
  }

  const records = stats?.records
  const hasRecords = !!(records && (records.biggestRound || records.longestNight || records.bestNight || records.mostSolos))

  const gameStats = selectedGameDetail ? computeGameStats(selectedGameDetail) : null

  const gameBidHeatmapItems: BidHeatmapItem[] = (gameStats?.bids ?? []).map((b) => ({ bidType: b.bidType, count: b.count }))
  const gameBidsTotal = gameBidHeatmapItems.reduce((sum, b) => sum + b.count, 0)

  const gameTrumpItems: BarListItem[] = buildTrumpItems(gameStats?.trumps ?? [])

  const gameMatrixPlayers = (selectedGameDetail?.players ?? []).map((p) => ({ id: p.userId, name: p.name }))
  const gameCellFor = (a: number, b: number) => {
    const entry = gameStats?.partners.find((p) => (p.a === a && p.b === b) || (p.a === b && p.b === a))
    return entry ? { points: entry.points, rounds: entry.rounds, won: entry.won } : null
  }
  const gameBidderPartnerCellFor = (bidderId: number, partnerId: number) => {
    const entry = gameStats?.bidderPartners.find((p) => p.bidderId === bidderId && p.partnerId === partnerId)
    return entry ? { count: entry.count, won: entry.won } : null
  }

  const biggestRound = gameStats?.records.biggestRound ?? null

  return (
    <Screen>
      <TopBar title={statsTexts.title} />

      {clubs.length > 1 && (
        <PickSegment
          options={clubs.map((c) => ({ value: c.id, label: c.name }))}
          value={clubId ?? clubs[0].id}
          onChange={setClubId}
        />
      )}

      <PickSegment
        options={[
          { value: 'season' as Mode, label: statsTexts.modeSeason },
          { value: 'game' as Mode, label: statsTexts.modeGame }
        ]}
        value={mode}
        onChange={setMode}
      />

      {mode === 'season' && years.length > 1 && (
        <PickSegment options={years.map((y) => ({ value: y, label: String(y) }))} value={year} onChange={setYear} />
      )}

      {mode === 'season' ? (
        <>
          {/* (a) Pointkurve */}
          <Panel>
            <b>{statsTexts.curveTitle}</b>
            {seasonCurveGame ? (
              <>
                <span className="text-[0.8em] text-text-2">{statsTexts.curveSubtitle(seasonCurveGame.name)}</span>
                <GameCurve
                  game={{ players: seasonCurveGame.players, rounds: seasonCurveGame.rounds }}
                  ariaLabel={statsTexts.curveSubtitle(seasonCurveGame.name)}
                />
              </>
            ) : (
              <p className="text-[0.85em] text-text-2">{statsTexts.curveNeedTwoRounds}</p>
            )}
          </Panel>

          {loadingStats && <p className="text-text-2">{statsTexts.loading}</p>}
          {statsError && <p className="text-red">{statsTexts.couldNotLoad}</p>}

          {stats && (
            <>
              {/* (b) Sæsonstilling */}
              <Panel>
                <b>{statsTexts.standingTitle}</b>
                {stats.standing.length === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.noGamesInYear(year)}</p>
                ) : (
                  <div className="grid gap-1.5">
                    <div className="grid grid-cols-[20px_1fr_44px_36px_36px] gap-2 text-[0.65rem] uppercase tracking-[0.04em] text-text-2/70">
                      <span />
                      <span />
                      <span className="text-right">{statsTexts.standingPoints}</span>
                      <span className="text-right">{statsTexts.standingGames}</span>
                      <span className="text-right">{statsTexts.standingWins}</span>
                    </div>
                    {[...stats.standing]
                      .sort((a, b) => b.points - a.points)
                      .map((s, i) => (
                        <div key={s.userId} className="grid grid-cols-[20px_1fr_44px_36px_36px] items-center gap-2 text-[0.85em]">
                          <span className="font-mono text-text-2">{i + 1}</span>
                          <span className="flex min-w-0 items-center gap-2">
                            <Avatar name={s.name} index={i} />
                            <span className="min-w-0 truncate">{s.name}</span>
                          </span>
                          <span className={`text-right font-mono font-semibold ${toneOf(s.points) === 'mint' ? 'text-mint' : toneOf(s.points) === 'red' ? 'text-red' : ''}`}>
                            {fmt(s.points)}
                          </span>
                          <span className="text-right text-text-2">{s.games}</span>
                          <span className="text-right text-text-2">{s.wins}</span>
                        </div>
                      ))}
                  </div>
                )}
              </Panel>

              {/* Spillere */}
              <Panel>
                <b>{statsTexts.playersTableTitle}</b>
                <PlayersStatsTable players={stats.players} />
              </Panel>

              {/* (c) Meldinger */}
              <Panel>
                <b>{statsTexts.bidsTitle}</b>
                {bidHeatmapItems.length === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.bidsEmpty}</p>
                ) : (
                  <>
                    <span className="text-[0.75em] text-text-2">{statsTexts.bidsTotal(bidsTotal)}</span>
                    <BidHeatmap items={bidHeatmapItems} ariaLabel={statsTexts.bidsTitle} />
                  </>
                )}
              </Panel>

              {/* Makkerskaber */}
              <Panel>
                <b>{statsTexts.bidderPartnersTitle}</b>
                <span className="text-[0.75em] text-text-2">{statsTexts.bidderPartnersHint}</span>
                {matrixPlayers.length < 2 || stats.bidderPartners.length === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.bidderPartnersEmpty}</p>
                ) : (
                  <CountMatrix
                    rows={matrixPlayers}
                    cols={matrixPlayers}
                    cellFor={bidderPartnerCellFor}
                    ariaLabel={statsTexts.bidderPartnersTitle}
                  />
                )}
              </Panel>

              {/* (d) Makkere */}
              <Panel>
                <b>{statsTexts.partnersTitle}</b>
                <span className="text-[0.75em] text-text-2">{statsTexts.partnersHint}</span>
                {matrixPlayers.length < 2 || stats.partners.length === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.partnersEmpty}</p>
                ) : (
                  <Matrix players={matrixPlayers} cellFor={cellFor} ariaLabel={statsTexts.partnersTitle} />
                )}
              </Panel>

              {/* (e) Trumf */}
              <Panel>
                <b>{statsTexts.trumpsTitle}</b>
                <BarList items={trumpItems} ariaLabel={statsTexts.trumpsTitle} />
              </Panel>

              {/* (f) Rekorder */}
              <Panel>
                <b>{statsTexts.recordsTitle}</b>
                {!hasRecords ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.recordsEmpty}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <StatTile
                      value={records?.biggestRound ? fmt(records.biggestRound.score) : statsTexts.none}
                      caption={statsTexts.recordBiggestRound}
                      tone={records?.biggestRound ? toneOf(records.biggestRound.score) : 'default'}
                      title={
                        records?.biggestRound
                          ? `${records.biggestRound.bidderName} · ${records.biggestRound.bidType} · ${records.biggestRound.gameName}`
                          : undefined
                      }
                    />
                    <StatTile
                      value={records?.longestNight ? statsTexts.roundsSuffix(records.longestNight.rounds) : statsTexts.none}
                      caption={statsTexts.recordLongestNight}
                      title={records?.longestNight?.gameName}
                    />
                    <StatTile
                      value={records?.bestNight ? fmt(records.bestNight.points) : statsTexts.none}
                      caption={statsTexts.recordBestNight}
                      tone={records?.bestNight ? toneOf(records.bestNight.points) : 'default'}
                      title={records?.bestNight ? `${records.bestNight.name} · ${records.bestNight.gameName}` : undefined}
                    />
                    <StatTile
                      value={records?.mostSolos ? String(records.mostSolos.count) : statsTexts.none}
                      caption={statsTexts.recordMostSolos}
                      title={records?.mostSolos?.name}
                    />
                  </div>
                )}
              </Panel>
            </>
          )}
        </>
      ) : (
        <>
          {/* Spil-liste */}
          <Panel>
            <b>{statsTexts.modeGame}</b>
            {sortedGamesInClub.length === 0 ? (
              <p className="text-[0.85em] text-text-2">{statsTexts.gameListEmpty}</p>
            ) : (
              <select
                id="stats-game"
                aria-label={statsTexts.modeGame}
                value={selectedGameId ?? ''}
                onChange={(e) => setSelectedGameId(Number(e.target.value))}
                className="wb-input min-h-11 w-full rounded-xl border border-text-2/20 px-3 py-2.5 text-text"
              >
                {sortedGamesInClub.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} · {formatDate(g.createdAt)}{g.status === 'active' ? ` · ${statsTexts.statusActive}` : ''}
                  </option>
                ))}
              </select>
            )}
          </Panel>

          {selectedGameDetail && (
            <>
              {/* Pointkurve */}
              <Panel>
                <b>{statsTexts.curveTitle}</b>
                {selectedGameDetail.rounds.length > 0 ? (
                  <GameCurve
                    game={{ players: selectedGameDetail.players, rounds: selectedGameDetail.rounds }}
                    ariaLabel={statsTexts.curveSubtitle(selectedGameDetail.name)}
                  />
                ) : (
                  <p className="text-[0.85em] text-text-2">{statsTexts.curveEmpty}</p>
                )}
              </Panel>

              {/* Stilling */}
              <Panel>
                <b>{statsTexts.gameStandingTitle}</b>
                <div className="grid gap-1.5">
                  {[...selectedGameDetail.players]
                    .sort((a, b) => b.totalScore - a.totalScore)
                    .map((p, i) => (
                      <div key={p.userId} className="flex items-center justify-between rounded-xl bg-ink/20 px-3 py-2">
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar name={p.name} index={i} />
                          <span className="min-w-0 truncate">{p.name}</span>
                        </span>
                        <span className={`font-mono font-semibold ${toneOf(p.totalScore) === 'mint' ? 'text-mint' : toneOf(p.totalScore) === 'red' ? 'text-red' : ''}`}>
                          {fmt(p.totalScore)}
                        </span>
                      </div>
                    ))}
                </div>
              </Panel>

              {/* Meldinger */}
              <Panel>
                <b>{statsTexts.bidsTitle}</b>
                {gameBidHeatmapItems.length === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.bidsEmpty}</p>
                ) : (
                  <>
                    <span className="text-[0.75em] text-text-2">{statsTexts.bidsTotal(gameBidsTotal)}</span>
                    <BidHeatmap items={gameBidHeatmapItems} ariaLabel={statsTexts.bidsTitle} />
                  </>
                )}
              </Panel>

              {/* Makkerskaber */}
              <Panel>
                <b>{statsTexts.bidderPartnersTitle}</b>
                <span className="text-[0.75em] text-text-2">{statsTexts.bidderPartnersHint}</span>
                {gameMatrixPlayers.length < 2 || (gameStats?.bidderPartners.length ?? 0) === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.bidderPartnersEmpty}</p>
                ) : (
                  <CountMatrix
                    rows={gameMatrixPlayers}
                    cols={gameMatrixPlayers}
                    cellFor={gameBidderPartnerCellFor}
                    ariaLabel={statsTexts.bidderPartnersTitle}
                  />
                )}
              </Panel>

              {/* Makkere */}
              <Panel>
                <b>{statsTexts.partnersTitle}</b>
                {gameMatrixPlayers.length < 2 || (gameStats?.partners.length ?? 0) === 0 ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.partnersEmpty}</p>
                ) : (
                  <Matrix players={gameMatrixPlayers} cellFor={gameCellFor} ariaLabel={statsTexts.partnersTitle} />
                )}
              </Panel>

              {/* Trumf */}
              <Panel>
                <b>{statsTexts.trumpsTitle}</b>
                <BarList items={gameTrumpItems} ariaLabel={statsTexts.trumpsTitle} />
              </Panel>

              {/* Spillere */}
              <Panel>
                <b>{statsTexts.playersTableTitle}</b>
                <PlayersStatsTable players={gameStats?.players ?? []} />
              </Panel>

              {/* Rekorder */}
              <Panel>
                <b>{statsTexts.recordsTitle}</b>
                {!biggestRound ? (
                  <p className="text-[0.85em] text-text-2">{statsTexts.recordsEmpty}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <StatTile
                      value={fmt(biggestRound.score)}
                      caption={statsTexts.recordBiggestRound}
                      tone={toneOf(biggestRound.score)}
                      title={`${biggestRound.bidderName} · ${biggestRound.bidType}`}
                    />
                  </div>
                )}
              </Panel>
            </>
          )}
        </>
      )}

      {/* (g) Din profil */}
      <Panel>
        <b>{statsTexts.profileTitle}</b>
        {!myStats ? (
          <p className="text-text-2">{statsTexts.loading}</p>
        ) : myStats.games === 0 ? (
          <p className="text-[0.85em] text-text-2">{statsTexts.profileNeedGames}</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <StatTile value={String(myStats.games)} caption={statsTexts.profileGames} />
            <StatTile value={String(myStats.rounds)} caption={statsTexts.profileRounds} />
            <StatTile value={fmt(myStats.totalPoints)} caption={statsTexts.profileTotalPoints} tone={toneOf(myStats.totalPoints)} />
            <StatTile
              value={pct(myStats.asBidder.winRate)}
              caption={statsTexts.profileAsBidder}
              title={statsTexts.wonOf(myStats.asBidder.won, myStats.asBidder.count)}
            />
            <StatTile
              value={pct(myStats.asPartner.winRate)}
              caption={statsTexts.profileAsPartner}
              title={statsTexts.wonOf(myStats.asPartner.won, myStats.asPartner.count)}
            />
            <StatTile
              value={pct(myStats.asOpponent.winRate)}
              caption={statsTexts.profileAsOpponent}
              title={statsTexts.wonOf(myStats.asOpponent.won, myStats.asOpponent.count)}
            />
            <StatTile value={statsTexts.wonOf(myStats.solos.won, myStats.solos.count)} caption={statsTexts.profileSolos} />
            <StatTile value={myStats.favoriteBid ?? statsTexts.none} caption={statsTexts.profileFavoriteBid} />
            <StatTile value={myStats.favoriteTrump ?? statsTexts.none} caption={statsTexts.profileFavoriteTrump} />
            <StatTile
              value={myStats.bestPartner?.name ?? statsTexts.none}
              caption={statsTexts.profileBestPartner}
              title={
                myStats.bestPartner
                  ? `${fmt(myStats.bestPartner.points)} · ${myStats.bestPartner.rounds} runder`
                  : statsTexts.profileEmptyBestPartner
              }
            />
            <StatTile
              value={myStats.bestNight ? fmt(myStats.bestNight.points) : statsTexts.none}
              caption={statsTexts.profileBestNight}
              tone={myStats.bestNight ? toneOf(myStats.bestNight.points) : 'default'}
              title={myStats.bestNight?.gameName ?? statsTexts.profileEmptyBestNight}
            />
          </div>
        )}
      </Panel>

      <BottomNav current="stats" />
    </Screen>
  )
}

export default Stats
